import React, { useContext, useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import { FaQrcode } from "react-icons/fa";
import { ordersApi, recentEarnings } from "../services/orders";
import { setAppBadge } from "../lib/appBadge";
import { useProfileDrawer } from "../lib/profileDrawer";
import { pulseBalanceTarget } from "../lib/flyToBalance";
import "./BottomTabBar.css";
import { absMediaUrl } from "../utils/mediaUrl";
import { useKeyboardOpen } from "../hooks/useKeyboardOpen";
import { trackTabBar } from "../lib/tabBarVars";
import { t as tr } from "../i18n";
import { DEFAULT_AVATAR_SRC } from "../lib/defaultAvatar";

// Routes the tab bar rides on. Public/onboarding/full-screen flows are absent
// so the bar self-hides there (same idea as ChatBotConditional in App.tsx).
// Each app route maps to the tab that should light up while you're on it —
// sub-screens (payout history, leaderboard) keep their parent tab active.
type TabName = "requests" | "qr" | "balance" | "profile";
// Left-to-right order in the bar — the sliding pill's index.
const TAB_ORDER: TabName[] = ["requests", "qr", "balance", "profile"];

const isFanTabRoute = (p: string) => p === "/fan" || p === "/fan/requests" || p === "/fan/leaderboard";

const TAB_FOR_PATH: { test: (p: string) => boolean; tab: TabName }[] = [
  { test: (p) => p === "/requests", tab: "requests" },
  { test: (p) => p.startsWith("/qr/"), tab: "qr" },
  { test: (p) => p === "/balance" || p === "/payout-history", tab: "balance" },
  // Profile hosts the former "More" menu (Leaderboard / Terms / Privacy /
  // account actions), so those sub-screens keep the Profile tab lit. Profile
  // itself is no longer a route — it's the #profile drawer over any tab.
  { test: (p) => p === "/leaderboard" || p === "/terms" || p === "/privacy", tab: "profile" },
  // A talent who paid on another artist's page (Discover, 1.5.3) lands on the
  // fans' My Requests like any fan — but keeps their own bar, so their app is
  // one tap away (FanTabBar steps aside for a signed-in talent). Profile is
  // lit: Discover lives there. Fans never see this bar (it needs a talent).
  { test: (p) => isFanTabRoute(p), tab: "profile" },
];

// One nudge per app open: set once the Balance icon has bumped, cleared when
// the app goes to the background.
let nudgedThisOpen = false;

function activeTab(pathname: string): TabName | null {
  const hit = TAB_FOR_PATH.find((r) => r.test(pathname));
  return hit ? hit.tab : null;
}

const BottomTabBar: React.FC = () => {
  const { user, isAuthenticated } = useContext(AuthContext);
  const { pathname } = useLocation();
  const barRef = useRef<HTMLDivElement>(null);
  const { isOpen: profileDrawerOpen, open: openProfileDrawer } = useProfileDrawer();

  const [pendingCount, setPendingCount] = useState(0);
  const [avatarBroken, setAvatarBroken] = useState(false);

  // While the Profile drawer is layered over a tab, the Profile slot is the
  // one that reads active — the underlying tab is still there, just pushed.
  const current = profileDrawerOpen ? "profile" : activeTab(pathname);
  // Gone while the keyboard is up: the webview shrinks for the keyboard, so
  // a fixed bar would sit on top of it (and --app-tabbar-h drops to 0 with
  // the bar, giving the page that space back).
  const keyboardOpen = useKeyboardOpen();
  const visible = isAuthenticated && !!user && current !== null && !keyboardOpen;

  // Reserve space for the fixed bar so content can't hide behind it: its
  // footprint and top edge, measured live (lib/tabBarVars) and cleared when
  // the bar unmounts.
  useEffect(() => {
    if (!visible) {
      document.documentElement.style.setProperty("--app-tabbar-h", "0px");
      document.documentElement.style.removeProperty("--app-bar-top");
      return;
    }
    const el = barRef.current;
    if (!el) return;
    return trackTabBar(el);
  }, [visible]);

  // Pending-request badge — same source + cadence as the Header burger badge.
  // The app-icon badge mirrors this exact count: pushes stamp
  // it while the app is closed, and this poll reconciles it whenever the app
  // is open (including clearing it once the queue is handled).
  useEffect(() => {
    if (!visible) return;
    let cancelled = false;
    const fetchCount = async () => {
      try {
        const data = await ordersApi.newRequests();
        if (cancelled) return;
        const n = Array.isArray(data) ? data.length : 0;
        setPendingCount(n);
        setAppBadge(n);
      } catch { /* transient — keep last value */ }
    };
    fetchCount();
    const t = setInterval(fetchCount, 30_000);
    // Refresh immediately when the app comes back to the foreground — a DJ
    // opening the app from the icon badge shouldn't wait out the 30s tick.
    const onVisible = () => {
      if (document.visibilityState === "visible") fetchCount();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      cancelled = true;
      clearInterval(t);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [visible]);

  // "Money is waiting" nudge (build 38): each time the app is opened or
  // brought back, the Balance icon gives the same little bump as a landed
  // play — only while the balance is above zero. Pure decoration.
  useEffect(() => {
    if (!visible) return;
    let cancelled = false;
    const nudge = async () => {
      if (nudgedThisOpen) return;
      nudgedThisOpen = true;
      try {
        // Requests and Balance fetch the balance themselves on landing — give
        // that a moment and reuse its answer; only ask the server when nobody
        // else has (at scale this is one request per app open, not two).
        await new Promise((r) => window.setTimeout(r, 1200));
        if (cancelled) {
          nudgedThisOpen = false; // the bar went away mid-wait — let the next mount try
          return;
        }
        const res = recentEarnings(10_000) ?? (await ordersApi.earnings());
        const hasFunds =
          Object.values(res?.byCurrency || {}).some((v) => Number(v?.total) > 0) || Number(res?.total) > 0 ||
          Object.values(res?.referralByCurrency || {}).some((v) => Number(v) > 0);
        if (hasFunds && !cancelled) pulseBalanceTarget();
      } catch { /* decoration — never matters */ }
    };
    nudge();
    const onVisibility = () => {
      if (document.visibilityState === "hidden") nudgedThisOpen = false;
      else nudge();
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [visible]);

  if (!visible) return null;

  return (
    <div className={`tabbar-wrap${isFanTabRoute(pathname) ? " tabbar-wrap--fan-page" : ""}`} ref={barRef}>
    <nav
      className="tabbar"
      aria-label={tr("bottomTabBar.primary")}
      style={{ "--tab-i": TAB_ORDER.indexOf(current as TabName) } as React.CSSProperties}
    >
      {/* The one green pill: it SLIDES to the active tab (founder, build 38)
          instead of each tab painting its own. Position is pure CSS off --tab-i. */}
      <span className="tabbar-pill" aria-hidden />
      <Link
        to="/requests"
        className={`tabbar-item ${current === "requests" ? "tabbar-item--active" : ""}`}
        aria-current={current === "requests" ? "page" : undefined}
      >
        <span className="tabbar-ic" aria-hidden>
          {/* Two-way arrows — requests flowing in/out (founder pick,
              full-width variant B). */}
          <svg
            width="21"
            height="21"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M5 7.5h14" />
            <path d="M15.8 4.3 19 7.5l-3.2 3.2" />
            <path d="M19 16.5H5" />
            <path d="M8.2 13.3 5 16.5l3.2 3.2" />
          </svg>
        </span>
        <span className="tabbar-label">{tr("bottomTabBar.requests")}</span>
        {pendingCount > 0 && (
          <span className="tabbar-badge" aria-hidden>
            {pendingCount > 99 ? "99+" : pendingCount}
          </span>
        )}
      </Link>

      <Link
        to={`/qr/${user?.slug ?? ""}`}
        className={`tabbar-item ${current === "qr" ? "tabbar-item--active" : ""}`}
        aria-current={current === "qr" ? "page" : undefined}
      >
        <span className="tabbar-ic" aria-hidden>
          <FaQrcode size={20} />
        </span>
        <span className="tabbar-label">{tr("bottomTabBar.qrCode")}</span>
      </Link>

      <Link
        to="/balance"
        className={`tabbar-item ${current === "balance" ? "tabbar-item--active" : ""}`}
        aria-current={current === "balance" ? "page" : undefined}
        // Landing pad for the "money flies into Balance" flight that fires when
        // a talent taps the $ on a queued card (src/lib/flyToBalance.ts).
        data-money-target="balance"
      >
        <span className="tabbar-ic" aria-hidden>
          {/* BALANCE ICON — dollar-in-a-circle. Money-clear without exposing the
              actual figure (the live amount was removed from the bar for security). */}
          <svg width="21" height="21" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1.41 16.09V20h-2.67v-1.93c-1.71-.36-3.16-1.46-3.27-3.4h1.96c.1 1.05.82 1.87 2.65 1.87 1.96 0 2.4-.98 2.4-1.59 0-.83-.44-1.61-2.67-2.14-2.48-.59-4.18-1.61-4.18-3.66 0-1.72 1.39-2.84 3.11-3.21V4h2.67v1.95c1.86.45 2.79 1.86 2.85 3.39H14.3c-.05-1.11-.64-1.87-2.22-1.87-1.5 0-2.4.68-2.4 1.64 0 .84.65 1.39 2.67 1.91s4.18 1.39 4.18 3.91c-.01 1.83-1.38 2.83-3.12 3.16z" />
          </svg>
        </span>
        <span className="tabbar-label">{tr("bottomTabBar.balance")}</span>
      </Link>

      {/* Profile is a drawer over the current tab, not a route:
          this slot opens it in place — you never leave the tab you're on. */}
      <button
        type="button"
        onClick={openProfileDrawer}
        className={`tabbar-item ${current === "profile" ? "tabbar-item--active" : ""}`}
        aria-haspopup="dialog"
        aria-expanded={profileDrawerOpen}
      >
        <span className="tabbar-ic" aria-hidden>
          {/* The talent's own photo (founder decision). avatarUrl rides the
              auth payloads; talent without a photo get the drawn default
              picture (1.5.2) — never a broken image (onError drops back to it
              too). */}
          <img
            className={`tab-avatar${current === "profile" ? " tab-avatar--ring" : ""}`}
            src={(user?.avatarUrl && !avatarBroken && absMediaUrl(user.avatarUrl)) || DEFAULT_AVATAR_SRC}
            alt=""
            onError={() => setAvatarBroken(true)}
          />
        </span>
        <span className="tabbar-label">{tr("bottomTabBar.profile")}</span>
      </button>
    </nav>
    </div>
  );
};

export default BottomTabBar;
